import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Briefcase,
  RefreshCw,
  Calendar,
  CheckCircle2,
  Clock,
  Gem,
  Handshake,
  Radio,
  Megaphone,
  Coins,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { DealRoomDealDetail } from '../components/DealRoomDealDetail';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
  closed_to_new_allocations: { label: 'Closed to new investment', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  live: { label: 'Live', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Radio },
  concluded: { label: 'Concluded', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: CheckCircle2 },
  written_off: { label: 'Written off', color: 'bg-red-100 text-red-700 border-red-200', icon: Clock },
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(amount || 0).replace('ZAR', 'R');

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
};

const DealRoomPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [eoiDialogDeal, setEoiDialogDeal] = useState(null);
  const [eoiAmount, setEoiAmount] = useState('');
  const [eoiNotes, setEoiNotes] = useState('');
  const [submittingEoi, setSubmittingEoi] = useState(false);
  const [detailDealId, setDetailDealId] = useState(null);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/investor/deal-room/deals`, { headers });
      setDeals(res.data.deals || []);
    } catch (error) {
      console.error('Failed to fetch Deal Room deals:', error);
      toast.error('Failed to load Deal Room deals');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const openEoiDialog = (deal) => {
    setEoiDialogDeal(deal);
    setEoiAmount('');
    setEoiNotes('');
  };

  const handleSubmitEoi = async () => {
    if (!eoiAmount || parseFloat(eoiAmount) <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    setSubmittingEoi(true);
    try {
      await axios.post(
        `${API_URL}/api/investor/deal-room/deals/${eoiDialogDeal.id}/eoi`,
        { amount: parseFloat(eoiAmount), notes: eoiNotes || null },
        { headers }
      );
      toast.success('Expression of interest submitted');
      setEoiDialogDeal(null);
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit expression of interest');
    } finally {
      setSubmittingEoi(false);
    }
  };

  const handleWithdrawEoi = async (dealId) => {
    if (!window.confirm('Withdraw your expression of interest for this deal?')) return;
    try {
      await axios.delete(`${API_URL}/api/investor/deal-room/deals/${dealId}/eoi`, { headers });
      toast.success('Expression of interest withdrawn');
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw');
    }
  };

  const openDeals = deals.filter(d => d.status === 'open').length;
  const totalTarget = deals.reduce((sum, d) => sum + (d.target_amount || 0), 0);
  const totalRaised = deals.reduce((sum, d) => sum + (d.total_allocated || 0), 0);
  const myDealsCount = deals.filter(d => d.my_allocation != null).length;

  return (
    <div className="space-y-6" data-testid="deal-room-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Deal Room</h1>
          <p className="text-[#6C757D] mt-1">Off-loanbook deals — tracked and reported separately from your fund returns</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchDeals} className="border-gray-200" data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]" data-testid="open-deals-count">{openDeals}</p>
                <p className="text-xs text-[#6C757D]">Open Deals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{formatCurrency(totalTarget)}</p>
                <p className="text-xs text-[#6C757D]">Total Funding Target</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Handshake className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{formatCurrency(totalRaised)}</p>
                <p className="text-xs text-[#6C757D]">Total Raised</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <Gem className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">{myDealsCount}</p>
                <p className="text-xs text-[#6C757D]">Your Deal Room Investments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deals Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-72 w-full" />)}
        </div>
      ) : deals.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <Gem className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-[#212529] font-medium">No Deal Room deals yet</p>
            <p className="text-[#6C757D] text-sm">Check back later for new opportunities</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {deals.map((deal) => {
            const statusConfig = STATUS_CONFIG[deal.status] || STATUS_CONFIG.open;
            const StatusIcon = statusConfig.icon;
            const progress = deal.progress_pct != null ? Math.min(deal.progress_pct, 100) : null;
            return (
              <Card
                key={deal.id}
                className="border-0 shadow-sm cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1D2D2A]/30"
                data-testid={`deal-card-${deal.id}`}
                role="button"
                tabIndex={0}
                onClick={() => setDetailDealId(deal.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailDealId(deal.id); } }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-lg bg-[#1D2D2A]/10 flex items-center justify-center">
                      <Gem className="h-5 w-5 text-[#1D2D2A]" />
                    </div>
                    <Badge className={`${statusConfig.color} border`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusConfig.label}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg mt-3">{deal.name}</CardTitle>
                  {deal.counterparty && <p className="text-xs text-[#6C757D]">{deal.counterparty}</p>}
                </CardHeader>
                <CardContent className="space-y-4">
                  {deal.description && <p className="text-sm text-[#6C757D]">{deal.description}</p>}

                  {deal.target_amount != null && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#6C757D]">Raised</span>
                        <span className="font-medium text-[#212529]">
                          {formatCurrency(deal.total_allocated)} / {formatCurrency(deal.target_amount)}
                        </span>
                      </div>
                      <Progress value={progress || 0} className="h-2" />
                      {progress != null && <p className="text-xs text-[#6C757D] mt-1">{progress}% funded</p>}
                    </div>
                  )}

                  {deal.debt_leg && (
                    <div data-testid={`deal-debt-leg-${deal.id}`}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#6C757D] flex items-center gap-1"><Coins className="h-3 w-3" /> Debt portion settled (first)</span>
                        <span className="font-medium text-[#212529]">
                          {formatCurrency(deal.debt_leg.repaid)} / {formatCurrency(deal.debt_leg.drawn)}
                        </span>
                      </div>
                      <Progress value={Math.min(deal.debt_leg.settled_pct || 0, 100)} className="h-2" />
                      <p className="text-xs text-[#6C757D] mt-1">
                        Total deal value {formatCurrency(deal.total_deal_value)} · the Deal Room portion settles after the debt
                      </p>
                    </div>
                  )}

                  {deal.expected_collection_amount != null && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#6C757D] flex items-center gap-1"><Coins className="h-3 w-3" /> Collected</span>
                        <span className="font-medium text-[#212529]">
                          {formatCurrency(deal.total_collected)} / {formatCurrency(deal.expected_collection_amount)}
                        </span>
                      </div>
                      <Progress value={Math.min(deal.collection_progress_pct || 0, 100)} className="h-2" />
                      {deal.collection_progress_pct != null && <p className="text-xs text-[#6C757D] mt-1">{deal.collection_progress_pct}% collected</p>}
                      {deal.collections?.expected_debt > 0 && (
                        <p className="text-xs text-[#6C757D]">
                          Debt portion {formatCurrency(deal.collections.collected_debt)} · Deal Room portion {formatCurrency(deal.collections.collected_manual)}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                    <div>
                      <p className="text-xs text-[#6C757D]">Return</p>
                      <p className="font-semibold text-[#212529] text-sm">
                        {deal.status === 'concluded'
                          ? `${deal.actual_return_pct}% actual`
                          : (deal.indicative_return_low != null
                            ? `${deal.indicative_return_low}–${deal.indicative_return_high}% indic.`
                            : (deal.indicative_return_note || 'TBC'))}
                      </p>
                    </div>
                    {deal.expected_settlement_date && (
                      <div>
                        <p className="text-xs text-[#6C757D] flex items-center gap-1"><Calendar className="h-3 w-3" /> Expected settlement</p>
                        <p className="font-semibold text-[#212529] text-sm">{formatDate(deal.expected_settlement_date)}</p>
                      </div>
                    )}
                  </div>

                  {deal.latest_update && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-[#6C757D] flex items-center gap-1 mb-1"><Megaphone className="h-3 w-3" /> Latest update</p>
                      <p className="text-sm text-[#212529]">{deal.latest_update.message}</p>
                      <p className="text-xs text-[#6C757D] mt-0.5">{formatDate(deal.latest_update.posted_at)}</p>
                    </div>
                  )}

                  {/* Your status on this deal */}
                  <div className="pt-2 border-t space-y-2" onClick={(e) => e.stopPropagation()}>
                    {deal.my_allocation != null && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border">
                        Your investment: {formatCurrency(deal.my_allocation)}
                      </Badge>
                    )}
                    {!deal.my_allocation && deal.my_eoi && (
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 border">
                          EOI {deal.my_eoi.status === 'submitted' ? 'Submitted' : 'Accepted'} — {formatCurrency(deal.my_eoi.amount)}
                        </Badge>
                        {deal.my_eoi.status === 'submitted' && (
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-red-500" onClick={() => handleWithdrawEoi(deal.id)}>
                            Withdraw
                          </Button>
                        )}
                      </div>
                    )}
                    {!deal.my_allocation && !deal.my_eoi && deal.status === 'open' && (
                      <Button size="sm" className="w-full" onClick={() => openEoiDialog(deal)} data-testid={`deal-eoi-btn-${deal.id}`}>
                        Express Interest
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => setDetailDealId(deal.id)}
                      data-testid={`deal-view-btn-${deal.id}`}
                    >
                      <Megaphone className="h-3.5 w-3.5 mr-1" />
                      {deal.update_count ? `View updates (${deal.update_count})` : 'View deal'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <DealRoomDealDetail dealId={detailDealId} token={token} onClose={() => setDetailDealId(null)} />

      {/* EOI dialog */}
      <Dialog open={!!eoiDialogDeal} onOpenChange={(v) => { if (!v) setEoiDialogDeal(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Express interest — {eoiDialogDeal?.name}</DialogTitle>
            <DialogDescription>
              Let us know how much you'd like to invest. This isn't a commitment — we'll follow up if we move forward.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Amount (ZAR) *</Label>
              <Input type="number" step="0.01" min="0" value={eoiAmount} onChange={e => setEoiAmount(e.target.value)} data-testid="eoi-amount" />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea value={eoiNotes} onChange={e => setEoiNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEoiDialogDeal(null)}>Cancel</Button>
            <Button onClick={handleSubmitEoi} disabled={submittingEoi} data-testid="eoi-submit">
              {submittingEoi ? 'Submitting…' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DealRoomPage;
